/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { cancelEvent } from '#utils/UNSAFE_visUtils';
import type { GanttCurrentTask, GanttRowData, GanttTaskData } from '../gantt.types';
import type { getRowDataLayout } from '../utils/dataLayoutUtils';
import {
  getAboveTaskNavigable,
  getBelowTaskNavigable,
  getNextTaskNavigable,
  getPrevTaskNavigable
} from '../utils/navigationUtils';

type UseGanttTaskNavigationOptions<
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
> = {
  rowDataLayout: ReturnType<typeof getRowDataLayout<K1, K2, D1, D2>>;
  currentTask: GanttCurrentTask<K2>['currentTask'];
  onCurrentTaskChange: GanttCurrentTask<K2>['onCurrentTaskChange'];
  isRTL: boolean;
  isDisabled?: boolean;
};

/**
 * Hook that handles row labels keyboard navigation.
 */
const useGanttTaskNavigation = <
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>({
  rowDataLayout,
  currentTask,
  onCurrentTaskChange,
  isRTL,
  isDisabled = false
}: UseGanttTaskNavigationOptions<K1, K2, D1, D2>) => {
  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key;
    switch (key) {
      case 'ArrowRight': {
        const nextTaskId =
          currentTask &&
          (!isRTL
            ? getNextTaskNavigable(currentTask.id, rowDataLayout)
            : getPrevTaskNavigable(currentTask.id, rowDataLayout));
        if (nextTaskId !== undefined) {
          onCurrentTaskChange({ id: nextTaskId, isFocused: true });
        }
        cancelEvent(event);
        break;
      }
      case 'ArrowLeft': {
        const prevTaskId =
          currentTask &&
          (!isRTL
            ? getPrevTaskNavigable(currentTask.id, rowDataLayout)
            : getNextTaskNavigable(currentTask.id, rowDataLayout));
        if (prevTaskId !== undefined) {
          onCurrentTaskChange({ id: prevTaskId, isFocused: true });
        }
        cancelEvent(event);
        break;
      }
      case 'ArrowUp': {
        const aboveTaskId = currentTask && getAboveTaskNavigable(currentTask.id, rowDataLayout);
        if (aboveTaskId !== undefined) {
          onCurrentTaskChange({ id: aboveTaskId, isFocused: true });
        }
        cancelEvent(event);
        break;
      }
      case 'ArrowDown': {
        const belowTaskId = currentTask && getBelowTaskNavigable(currentTask.id, rowDataLayout);
        if (belowTaskId !== undefined) {
          onCurrentTaskChange({ id: belowTaskId, isFocused: true });
        }
        cancelEvent(event);
        break;
      }
    }
  };

  return isDisabled ? {} : { onKeyDown };
};

export { useGanttTaskNavigation };
